/**
 * VerifiedInquiryDialog &mdash; the gate buyers use to contact a free-tier
 * US kennel. Charges $9 via Stripe Checkout (waived for verified
 * Armstrong accounts and grandfathered kennels).
 *
 * Wired from `PublicBreeder.jsx` next to the Share + Watch CTAs.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheckIcon as ShieldCheck, EnvelopeSimpleIcon as EnvelopeSimple } from "@phosphor-icons/react";

export default function VerifiedInquiryDialog({
  open, onOpenChange, breeder,
}) {
  const slug = breeder?.slug || breeder?.id;
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "", buyer_phone: "",
    buyer_city: "", breed_interest: "", notes: "",
  });
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!ack) {
      toast.error("Please acknowledge the venue disclaimer.");
      return;
    }
    if (!form.buyer_name || !form.buyer_email) {
      toast.error("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/kennel/${slug}/inquire`, {
        ...form, venue_acknowledged: true,
      });
      if (r.data?.requires_buyer_payment && r.data?.stripe_checkout_url) {
        toast.info(
          `Verified intro fee ($${r.data.amount_usd}) &mdash; redirecting to checkout…`
        );
        window.location.assign(r.data.stripe_checkout_url);
        return;
      }
      toast.success(r.data?.message || "Sent.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send inquiry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="kennel-verified-inquiry-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">
            Contact {breeder?.kennel_name || breeder?.name}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            We&rsquo;ll forward your details to the breeder. They reply
            directly to your email &mdash; Armstrong is just the venue.
          </DialogDescription>
        </DialogHeader>

        {breeder?.contact_gated && (
          <div className="bg-[#FBF7EC] border border-[#E5D08A] rounded-lg p-3 text-[12px] leading-relaxed mt-2">
            <ShieldCheck
              size={16}
              weight="duotone"
              className="text-[#8A6A00] inline mb-0.5 mr-1"
            />
            <strong>Verified Inquiry &middot; $9 one-time</strong> &mdash; we charge a
            small verification fee so this breeder only sees serious
            buyers. Logged-in Armstrong members get free verified
            inquiries.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Input
            placeholder="Your full name" value={form.buyer_name}
            onChange={set("buyer_name")}
            data-testid="kennel-inquiry-name"
          />
          <Input
            placeholder="Your email" type="email" value={form.buyer_email}
            onChange={set("buyer_email")}
            data-testid="kennel-inquiry-email"
          />
          <Input
            placeholder="Phone (optional)" value={form.buyer_phone}
            onChange={set("buyer_phone")}
          />
          <Input
            placeholder="Your city" value={form.buyer_city}
            onChange={set("buyer_city")}
          />
          <Input
            placeholder="Breed interest" value={form.breed_interest}
            onChange={set("breed_interest")}
            data-testid="kennel-inquiry-breed"
          />
        </div>
        <Textarea
          placeholder="Notes for the breeder (timing, family setup, what you&rsquo;re looking for)"
          rows={3} value={form.notes} onChange={set("notes")}
          className="mt-2"
        />
        <label className="flex gap-2 items-start mt-2 text-[11px] leading-relaxed text-[#5C5C5C]">
          <Checkbox
            checked={ack}
            onCheckedChange={(v) => setAck(!!v)}
            data-testid="kennel-inquiry-ack"
          />
          <span>
            I understand Armstrong K9 Registry is a venue. I will verify
            credentials and contract directly with the breeder. Armstrong
            does not warrant the breeder, the dogs, or the transaction.
          </span>
        </label>
        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="rounded-full bg-crimson hover:bg-ink text-white"
            disabled={busy || !ack}
            onClick={submit}
            data-testid="kennel-inquiry-submit"
          >
            <EnvelopeSimple size={14} className="mr-1.5" />
            {busy ? "Sending…" : "Send Verified Inquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
